import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { recentsKeys } from '~/lib/query-keys';
import type { AppSearchHit } from './use-apps-search';

/**
 * FRONT-2391 — the command palette's "Recent activity" read: six blended
 * (recorded-open ∪ own-recently-updated) sections for the signed-in caller,
 * shown ABOVE the Navigate group whenever the search box is empty. Mirrors
 * `src/recents/service.ts`'s
 * `RecentActivity`/`RecentChatWire`/`RecentProjectWire`/`RecentKnowledgeWire`/
 * `RecentAppWire` wire shapes by hand — the frontend can't import backend
 * modules across the build boundary (same reasoning `AppSearchHit` above
 * gives for its own hand-mirrored shape).
 */

export interface RecentChatHit {
  id: string;
  sessionId: string;
  title: string | null;
  lastActiveAt: string;
  /** The first user message, collapsed to one line and trimmed server-side
   *  (`src/recents/service.ts`'s `previewLine`) to at most 140 chars — plain
   *  text, never HTML. `null` when the thread has no user message yet. */
  preview: string | null;
}

export interface RecentProjectHit {
  id: string;
  name: string;
  /** ISO timestamp — `max(recorded-open, own updatedAt)`, i.e. "last
   *  opened", blended with "last touched" so the list isn't empty on day
   *  one. Not necessarily the container's own `updatedAt`. */
  at: string;
}

export interface RecentKnowledgeHit {
  kind: 'file' | 'document';
  /** `user_files.id` for a file, `memory_documents.filename` for a
   *  document. */
  id: string;
  title: string;
  at: string;
  /** Set only for a `kind: 'document'` row that is a coding-session
   *  journal — the resume handle `recentKnowledgeToDocumentMeta`
   *  (`command-palette.tsx`) needs to resume the session rather than
   *  opening the journal read-only (FRONT-2391). Always `null` for a
   *  `kind: 'file'` row. */
  codingSessionId: string | null;
}

/** Same wire row `/api/apps-search` returns, plus the blended "last opened"
 *  timestamp — reuses `AppSearchHit` rather than redeclaring its fields, so
 *  the two can never drift apart. */
export type RecentAppHit = AppSearchHit & { at: string };

export interface RecentActivity {
  chats: RecentChatHit[];
  apps: RecentAppHit[];
  projects: RecentProjectHit[];
  knowledge: RecentKnowledgeHit[];
  sketches: RecentAppHit[];
  decks: RecentAppHit[];
}

const EMPTY: RecentActivity = {
  chats: [],
  apps: [],
  projects: [],
  knowledge: [],
  sketches: [],
  decks: [],
};

/**
 * `kinds` only gates the `apps`/`sketches`/`decks` sections on the backend
 * (`chats`/`projects`/`knowledge` are always computed) — same semantics as
 * `useAppsSearch`'s own `kinds`, and the palette passes it the SAME value
 * that hook receives, so a workspace with Slides/Sketches off never asks for
 * (and never renders) those Recent sections either.
 *
 * `staleTime: 0` plus `enabled` toggling false→true is the refetch
 * mechanism: the palette flips `enabled` off when it closes (and while a
 * real search query is active) and back on when it reopens with an empty
 * box, and a zero staleTime means data is always considered stale by the
 * time that happens, so TanStack refetches rather than serving a
 * possibly-minutes-old list.
 */
export function useRecentActivity(opts?: {
  enabled?: boolean;
  kinds?: Array<'app' | 'deck' | 'sketch'>;
}) {
  // Sorted for the same reason `useAppsSearch` sorts its own `kinds` — the
  // same set in a different order must be one cache entry, not two.
  const kinds = opts?.kinds ? [...opts.kinds].sort().join(',') : undefined;

  const query = useQuery<RecentActivity>({
    queryKey: recentsKeys.list(kinds),
    queryFn: () => {
      const params = new URLSearchParams();
      if (kinds !== undefined) params.set('kinds', kinds);
      const qs = params.toString();
      return api.get<RecentActivity>(`/api/recents${qs ? `?${qs}` : ''}`);
    },
    enabled: opts?.enabled,
    staleTime: 0,
  });

  return {
    // A settled-but-empty result and an in-flight one both read as empty
    // here — same "the palette gates its own sections on the loading flag,
    // not on emptiness" reasoning `useAppsSearch` documents, though here the
    // palette doesn't even need a loading flag: while the first request is
    // in flight every section's array is empty, so nothing renders (no
    // spinner, no skeleton) until real data lands.
    data: query.data ?? EMPTY,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
  };
}
